import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Subscription, of } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { MortgageService } from '../../../mortgage/services/mortgage/mortgage.service';
import { GoogleTagManagerModel } from '../../../shared/models/gtm.models';
import { PreSimulatorModel } from '../../../shared/models/pre-simulator.model';
import { GtmService } from '../../../shared/services/gtm/gtm.service';
import { UserDataService } from '../../../shared/services/user-data/user-data.service';
import { ES_POSTAL_CODE_REGEXP } from '../../../shared/utils/constants';
import { SliderAmountInputComponent } from '../../../vass-core/components/slider-amount-input/slider-amount-input.component';
import { CCAAModel } from '../../models/ccaa.model';
import { ConfigInputPreSimulatorModel } from '../../models/config-input-pre-simulator.model';
import { MortgageChartModel } from '../../models/mortgage-chart.model';
import { SimulateResultData } from '../../models/ui/simulate-result-data.model';
import { SimulatorService } from '../../services/simulator/simulator.service';

@Component({
  selector: 'app-pre-simulator-form',
  templateUrl: './pre-simulator-form.component.html',
  styleUrls: ['./pre-simulator-form.component.scss'],
  standalone: false,
})
export class PreSimulatorFormComponent implements OnInit, OnDestroy {
  @ViewChild('mortgageAmountInput')
  mortgageAmountInput: SliderAmountInputComponent;
  @ViewChild('propertyPriceInput')
  propertyPriceInput: SliderAmountInputComponent;
  @ViewChild('termInYearsInput') termInYearsInput: SliderAmountInputComponent;
  @ViewChild('monthlyIncomeInput')
  monthlyIncomeInput: SliderAmountInputComponent;

  @Output() onSimulate: EventEmitter<SimulateResultData> = new EventEmitter();
  @Output() sendForm: EventEmitter<any> = new EventEmitter();

  @Input() isAdvanceSimulator: boolean;

  public simulatorForm: UntypedFormGroup;
  public listCCAA: Array<CCAAModel>;
  public showButtonSimulate: boolean;
  public simulateResult: SimulateResultData;
  public configInputs: ConfigInputPreSimulatorModel;
  public sizeBar: MortgageChartModel;
  public totalExpenses: number;

  private changesSimulator$: Subscription;
  private requestSimulator$: Subscription;
  private resetForm$: Subscription;

  private maxFinancing: number;
  private maxMortgageAmount: number = 1000000;

  public postalCode = new UntypedFormControl(
    '',
    [
      Validators.maxLength(5),
      Validators.minLength(5),
      Validators.pattern(ES_POSTAL_CODE_REGEXP),
    ],
    this.validatePostalCode(),
  );

  constructor(
    private fb: UntypedFormBuilder,
    private _simulatorService: SimulatorService,
    private _userDataService: UserDataService,
    private gtm: GtmService,
    private _mortgageService: MortgageService,
  ) {}

  ngOnInit() {
    this.resetChart();
    this._simulatorService.getConfigInputs().subscribe((config) => {
      this.configInputs = config;
      this.maxFinancing = config.maxFinancing;
      this.maxMortgageAmount = config.mortgageAmount.max;
      this.createSimulatorForm();
      this.setSimulatorForm();

      this._userDataService.subscribeChanges().subscribe((change) => {
        if (change) {
          const userData = this._userDataService.getUserData();
          this.simulatorForm.patchValue(userData.level1Data);
        }
      });
    });
    this.resetForm$ = this._simulatorService.getOnResetForm().subscribe((data) => {
      if (data) {
        this.resetForm();
        this.simulateResult = this.resetSimulateResult();
      }
    });
    this.simulateResult = this.resetSimulateResult();
    this.showButtonSimulate = true;
    this.getCCAA();
  }

  private resetSimulateResult() {
    return {
      isOK: false,
    } as SimulateResultData;
  }
  createSimulatorForm() {
    this.simulatorForm = this.fb.group({
      location: ['', this.isAdvanceSimulator ? null : Validators.required],
      isHabitualHousing: [{ value: true, disabled: true }, Validators.required],
      isNewHome: [false, Validators.required],
      isSubsidizedHousing: [false, Validators.required],
      propertyPrice: [
        '0',
        this.isAdvanceSimulator
          ? [Validators.min(1), Validators.max(this.configInputs.propertyPrice.max)]
          : [
              Validators.required,
              Validators.min(1),
              Validators.max(this.configInputs.propertyPrice.max),
            ],
      ],
      mortgageAmount: [
        '0',
        this.isAdvanceSimulator
          ? [Validators.min(1), Validators.max(this.configInputs.mortgageAmount.max)]
          : [
              Validators.required,
              Validators.min(1),
              Validators.max(this.configInputs.mortgageAmount.max),
            ],
      ],
      savingsMade: [
        '0',
        this.isAdvanceSimulator
          ? [Validators.maxLength(8)]
          : [Validators.required, Validators.maxLength(8)],
      ],
      termInYears: [
        this.configInputs.termInYears.min,
        this.isAdvanceSimulator
          ? [
              Validators.min(this.configInputs.termInYears.min),
              Validators.max(this.configInputs.termInYears.max),
            ]
          : [
              Validators.required,
              Validators.min(this.configInputs.termInYears.min),
              Validators.max(this.configInputs.termInYears.max),
            ],
      ],
      monthlyIncome: [
        '0',
        this.isAdvanceSimulator
          ? [Validators.min(1), Validators.max(this.configInputs.monthlyIncome.max)]
          : [
              Validators.required,
              Validators.min(1),
              Validators.max(this.configInputs.monthlyIncome.max),
            ],
      ],
    });
    if (this.isAdvanceSimulator) {
      this.simulatorForm.controls.monthlyIncome.disable();
    }
    this.subscribeChangesForm();
    this.sendForm.emit(this.simulatorForm);
  }

  get propertyPrice() {
    return this.simulatorForm.get('propertyPrice')
      ? this.convertNum(this.simulatorForm.get('propertyPrice').value)
      : 0;
  }

  get mortgageAmount() {
    return this.simulatorForm.get('mortgageAmount')
      ? this.convertNum(this.simulatorForm.get('mortgageAmount').value)
      : 0;
  }

  get termInYears() {
    return this.simulatorForm.get('termInYears')
      ? this.convertNum(this.simulatorForm.get('termInYears').value)
      : 0;
  }

  setSimulatorForm() {
    const userData = this._userDataService.getUserData();
    if (userData) {
      this.simulatorForm.patchValue(userData.level1Data);
    }
  }

  subscribeChangesForm() {
    this.changesSimulator$ = this.simulatorForm.controls.propertyPrice.valueChanges
      .pipe(debounceTime(600))
      .subscribe((value) => {
        const max =
          this.convertNum(value) * this.maxFinancing > this.maxMortgageAmount
            ? this.maxMortgageAmount
            : this.convertNum(value) * this.maxFinancing;
        this.configInputs.mortgageAmount.max = max;
        this.simulatorForm.controls.mortgageAmount.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(this.configInputs.mortgageAmount.max),
        ]);
        this.simulatorForm.controls.mortgageAmount.updateValueAndValidity();

        if (
          this.convertNum(this.simulatorForm.controls.mortgageAmount.value) >
          this.configInputs.mortgageAmount.max
        ) {
          this.simulatorForm.controls.mortgageAmount.setValue(
            this.configInputs.mortgageAmount.max,
          );
        }
        if (!!this.mortgageAmountInput) {
          this.mortgageAmountInput.resetcomponet(
            this.simulatorForm.controls.mortgageAmount.value.toString(),
          );
        }
        this.simulatorForm.controls.savingsMade.setValue(
          this.convertNum(this.simulatorForm.controls.propertyPrice.value) -
            this.convertNum(this.simulatorForm.controls.mortgageAmount.value),
        );
        this.calculateBars();
      });

    this.simulatorForm.controls.mortgageAmount.valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.simulatorForm.controls.savingsMade.setValue(
          this.convertNum(this.simulatorForm.controls.propertyPrice.value) -
            this.convertNum(this.simulatorForm.controls.mortgageAmount.value),
        );
        this.calculateBars();
      });
  }

  convertNum(value: string) {
    return typeof value === 'number'
      ? value
      : parseFloat(value.toString().split('.').join(''));
  }

  getCCAA() {
    this._simulatorService.getCCAA().subscribe(
      (data) => {
        this.listCCAA = data;
      },
      (err) => {},
    );
  }

  firstSimulate() {
    if (this.simulatorForm.valid) {
      this.onSendEvent({
        event: 'interaccion',
        eAction: 'simular_hipoteca',
        eLabel: 'click',
      });
      this.changesSimulator$ = this.simulatorForm.valueChanges
        .pipe(debounceTime(600))
        .subscribe((change) => {
          if (this.requestSimulator$) {
            this.requestSimulator$.unsubscribe();
          }
          this.onSendEvent({
            event: 'interaccion',
            eAction: 'simular_hipoteca',
            eLabel: 'automatic',
          });
          this.simulate();
        });
    } else {
      for (const campo in this.simulatorForm.controls) {
        this.simulatorForm.controls[campo].markAsTouched();
      }
    }
  }

  simulate() {
    if (this.simulatorForm.valid) {
      const body: PreSimulatorModel = {
        isNewHome: this.simulatorForm.controls.isNewHome.value,
        propertyPrice: this.convertNum(
          this.simulatorForm.controls.propertyPrice.value,
        ).toString(),
        savingsMade: this.convertNum(
          this.simulatorForm.controls.savingsMade.value,
        ).toString(),
        isHabitualHousing: this.simulatorForm.controls.isHabitualHousing.value,
        isSubsidizedHousing: this.simulatorForm.controls.isSubsidizedHousing.value,
        operationType: 1,
        mortgageAmount: this.convertNum(
          this.simulatorForm.controls.mortgageAmount.value,
        ).toString(),
        termInYears: this.convertNum(
          this.simulatorForm.controls.termInYears.value,
        ).toString(),
        monthlyIncome: this.convertNum(
          this.simulatorForm.controls.monthlyIncome.value,
        ).toString(),
        location: this.convertNum(this.simulatorForm.controls.location.value).toString(),
      };
      this._userDataService.setLevel1Data(body);

      let result: SimulateResultData = {} as SimulateResultData;
      result.formValues = this.simulatorForm.value;
      result.formValues.mortgageAmount = this.convertNum(
        this.simulatorForm.value.mortgageAmount,
      );
      result.isOK = true;
      this.showButtonSimulate = false;

      // emito un objeto sin datos de simulacion para que salte la precarga
      this.onSimulate.emit(result);

      this.requestSimulator$ = this._simulatorService.simular(body).subscribe(
        (simulate) => {
          result.mortgageResult = simulate.mortgageResult;
          result.formValues = this.simulatorForm.value;
          result.formValues.mortgageAmount = this.convertNum(body.mortgageAmount);
          this.simulateResult = result;
          this.showButtonSimulate = false;
          this.onSimulate.emit(result);
          this.totalExpenses =
            parseFloat(result.mortgageResult.expenses.trading.total) +
            parseFloat(result.mortgageResult.expenses.mortgage.total);
          this.calculateBars();
        },
        (err) => {
          this.changesSimulator$ ? this.changesSimulator$.unsubscribe() : null;
          this.showButtonSimulate = true;
          this.totalExpenses = null;
          this.onSimulate.emit(null);
          this.calculateBars();
        },
      );
    }
  }

  calculateBars() {
    if (this.convertNum(this.simulatorForm.controls.mortgageAmount.value) > 0) {
      const savingsMade =
        this.convertNum(this.simulatorForm.controls.propertyPrice.value) -
        this.convertNum(this.simulatorForm.controls.mortgageAmount.value);
      this.sizeBar.moneyBar.barsavingsMade =
        (savingsMade * 100) /
        (savingsMade + this.convertNum(this.simulatorForm.controls.mortgageAmount.value));
      // Fijar minimo que se pondá de color rojo cuando sea menor a un porcentaje.
      // this.sizeBar.moneyBar.minBarSavingsMade = this.sizeBar.moneyBar.barsavingsMade > 30 ? 0 : 30 - this.sizeBar.moneyBar.barsavingsMade;
    } else {
      this.resetChart();
    }
    this.sizeBar.moneyBar.barMortgage =
      100 -
      this.sizeBar.moneyBar.barsavingsMade -
      this.sizeBar.moneyBar.minBarSavingsMade;

    if (!!this.totalExpenses) {
      const totalSize =
        this.convertNum(this.simulatorForm.controls.propertyPrice.value) +
        this.totalExpenses;
      this.sizeBar.barHouse =
        (this.convertNum(this.simulatorForm.controls.propertyPrice.value) * 100) /
        totalSize;
      this.sizeBar.barSpending = 100 - this.sizeBar.barHouse;
    }
  }

  resetChart() {
    this.sizeBar = {
      moneyBar: {
        barMortgage: 50,
        barsavingsMade: 50,
        minBarSavingsMade: 0,
      },
      barHouse: 50,
      barSpending: 50,
    };
  }

  resetForm() {
    this.changesSimulator$.unsubscribe();
    if (this.changesSimulator$) {
      this.changesSimulator$.unsubscribe();
    }
    this.simulatorForm.reset();
    this.subscribeChangesForm();
    this.showButtonSimulate = true;
    this.postalCode.setValue('');

    const resetValues = new PreSimulatorModel();
    this.simulatorForm.patchValue(resetValues);

    // Reiniciar los componentes del core, SliderAmountInputComponent
    this.mortgageAmountInput.resetcomponet('160000');
    this.propertyPriceInput.resetcomponet('200000');
    this.termInYearsInput.resetcomponet('25');
    this.monthlyIncomeInput.resetcomponet('0');

    this.sizeBar = {
      moneyBar: {
        barMortgage: 50,
        barsavingsMade: 50,
        minBarSavingsMade: 0,
      },
      barHouse: 50,
      barSpending: 50,
    };
  }

  openToast(id: string) {
    const elementHTML = document.getElementById(id);
    elementHTML.focus();
  }

  onSendEvent(event: GoogleTagManagerModel) {
    this.gtm.sendEventGTM(event);
  }

  private validatePostalCode() {
    return ({ value }) => {
      if (
        value &&
        value !== '' &&
        value.match(ES_POSTAL_CODE_REGEXP)
        // && this.currentValue !== value
      ) {
        return this._mortgageService.validatePostalCode(value).pipe(
          map((data) => {
            if (data.noValidPostalCode) {
              this.postalCode.markAsTouched();
            }
            return data;
          }),
        );
      } else {
        return of(null);
      }
    };
  }

  ngOnDestroy(): void {
    if (this.changesSimulator$) {
      this.changesSimulator$.unsubscribe();
    }
    this.resetForm$.unsubscribe();
  }
}
